import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  DOCUMENT
} from "@angular/core";

import { Platform } from "@angular/cdk/platform";
import { interval, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { FuseConfigService } from "@fuse/services/config.service";
import { FuseNavigationService } from "@fuse/components/navigation/navigation.service";
import { FuseSidebarService } from "@fuse/components/sidebar/sidebar.service";
import { FuseSplashScreenService } from "@fuse/services/splash-screen.service";
import { FuseTranslationLoaderService } from "@fuse/services/translation-loader.service";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { locale as navigationEnglish } from "../@ksm-core/shared/multilingual/en";
import { locale as navigationHindi } from "../@ksm-core/shared/multilingual/hi";

import { navigation } from "app/navigation/navigation";

import { SnackBarService } from "../@ksm-core/shared/components/snackbar/snack-bar.service";
import { AuthService } from "../@tpl-auth/services/auth.service";
import { PushNotificationService } from "../@ksm-core/shared/services/push-notification.service";
import { CordovaService } from "../@ksm-core/shared/services/cordova.service";
import { LanguageTranslateService } from "../@ksm-core/shared/services/language-translate.service";
import { AppSettingConstant } from "../@ksm-core/shared/constants/app-setting.constant";
import { Language } from "../@ksm/language/model/language.model";
import { LocalStorageConstant } from "../@tpl-auth/constants/local-storage.constant";
import { ValuesConstant } from "../@ksm-core/shared/constants/values.constant";
import {MatDialog,MatDialogRef } from '@angular/material/dialog';
import { UpdateAppDialogComponent } from "@ksm-core/shared/components/update-app-dialog/update-app-dialog.component";
import { FuseProgressBarComponent } from "../@fuse/components/progress-bar/progress-bar.component";
import { VerticalLayout1Component } from "./layout/vertical/layout-1/layout-1.component";
import { VerticalLayout2Component } from "./layout/vertical/layout-2/layout-2.component";
import { VerticalLayout3Component } from "./layout/vertical/layout-3/layout-3.component";

// noinspection ChainedFunctionCallJS,OverlyComplexFunctionJS
@Component({
    selector: "app",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
    imports: [FuseProgressBarComponent, VerticalLayout1Component, VerticalLayout2Component, VerticalLayout3Component]
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  fuseConfig: any;
  navigation: any;

  public selectedLanguage: Language;
  public languages: Language[];
  // Private
  private unsubscribeAll: Subject<any>;
  private dialogRef: MatDialogRef<UpdateAppDialogComponent>;
  public showUpdateModal : boolean = false;

  private static setGoogleMapLanguage(): void {
    if (typeof (window as any).google !== 'undefined' && (window as any).google.maps) {
      return;
    }

    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => {
      });
      return;
    }

    const googleMapScript = document.createElement("script");
    const language = localStorage.getItem(LocalStorageConstant.languageKey) || ValuesConstant.en;
    const apiKey = AppSettingConstant.agmApiKey;
    googleMapScript.src = `https://maps.googleapis.com/maps/api/js?v=quarterly&key=${apiKey}&libraries=places&language=${language}&callback=initGoogleMaps`;
    (window as any).initGoogleMaps = () => {
      delete (window as any).initGoogleMaps;
    };
    googleMapScript.onerror = () => {
      delete (window as any).initGoogleMaps;
    };
    document.head.appendChild(googleMapScript);
  }

  /**
   * Constructor
   *
   * @param {DOCUMENT} document
   * @param {FuseConfigService} fuseConfigService
   * @param {FuseNavigationService} fuseNavigationService
   * @param {FuseSidebarService} fuseSidebarService
   * @param {FuseSplashScreenService} fuseSplashScreenService
   * @param {FuseTranslationLoaderService} fuseTranslationLoaderService
   * @param {Platform} platform
   * @param {TranslateService} translateService
   * @param languageTranslateService
   * @param snackBarService
   * @param authService
   * @param cordovaService
   * @param pushNotificationService
   * @param translatePipe
   */
  constructor(
    @Inject(DOCUMENT) private document: any,
    private fuseConfigService: FuseConfigService,
    private fuseNavigationService: FuseNavigationService,
    private fuseSidebarService: FuseSidebarService,
    private fuseSplashScreenService: FuseSplashScreenService,
    private fuseTranslationLoaderService: FuseTranslationLoaderService,
    private translateService: TranslateService,
    private platform: Platform,
    private languageTranslateService: LanguageTranslateService,
    private snackBarService: SnackBarService,
    private authService: AuthService,
    private cordovaService: CordovaService,
    private pushNotificationService: PushNotificationService,
    private translatePipe: TranslatePipe,
    private _elementRef: ElementRef,
    public dialog: MatDialog
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {

    const isCordovaAvailable = typeof window['cordova'] !== 'undefined';

    if (isCordovaAvailable && (this.platform.IOS || this.platform.ANDROID)) {
      document.addEventListener("deviceready", () => {
        // Cordova StatusBar plugin initialization
        if (window['StatusBar']) {
          window['StatusBar'].overlaysWebView(false);
          window['StatusBar'].backgroundColorByHexString('#516cc1');
        }
        this.constructorMethods();
        this.ngOnInitMethods();
        setTimeout(() => {
          this.fuseSplashScreenService.hide();
        }, 1000);
      }, false);
    } else {
      this.constructorMethods();
      this.ngOnInitMethods();
      setTimeout(() => {
        this.fuseSplashScreenService.hide();
      }, 1000);
    }
  }
  constructorMethods(): void {
    this.checkUserStatus();
    // Get default navigation
    this.navigation = navigation;

    // Register the navigation to the service
    this.fuseNavigationService.register("main", this.navigation);

    // Set the main navigation as our current navigation
    this.fuseNavigationService.setCurrentNavigation("main");
    /**
     * ----------------------------------------------------------------------------------------------------
     * ngxTranslate Fix Start
     * ----------------------------------------------------------------------------------------------------
     */

    /**
     * If you are using a language other than the default one, i.e. Turkish in this case,
     * you may encounter an issue where some of the components are not actually being
     * translated when your app first initialized.
     *
     * This is related to ngxTranslate module and below there is a temporary fix while we
     * are moving the multi language implementation over to the Angular's core language
     * service.
     */

    // Set the default language to 'en' and then back to 'tr'.
    // '.use' cannot be used here as ngxTranslate won't switch to a language that's already
    // been selected and there is no way to force it, so we overcome the issue by switching
    // the default language back and forth.
    /**
     * setTimeout(() => {
     * this.translateService.setDefaultLang('en');
     * this.translateService.setDefaultLang('tr');
     * });
     */

    /**
     * ----------------------------------------------------------------------------------------------------
     * ngxTranslate Fix End
     * ----------------------------------------------------------------------------------------------------
     */

    // Add is-mobile class to the body if the platform is mobile
    if (this.platform.ANDROID || this.platform.IOS) {
      this.document.body.classList.add("is-mobile");
    }

    // Set the private defaults
    this.unsubscribeAll = new Subject();
  }

  ngOnInitMethods(): void {
    this._elementRef.nativeElement.removeAttribute("ng-version");
    this.fuseConfigService.config
      .pipe(takeUntil(this.unsubscribeAll))
      .subscribe((config) => {
        this.fuseConfig = config;

        // Boxed
        if (this.fuseConfig?.layout?.width === "boxed") {
          this.document.body.classList.add("boxed");
        } else {
          this.document.body.classList.remove("boxed");
        }

        // Color theme - Use normal for loop for IE11 compatibility
        for (const className of this.document.body.classList) {
          if (className.startsWith("theme-")) {
            this.document.body.classList.remove(className);
          }
        }
        this.document.body.classList.add(this.fuseConfig?.colorTheme);
      });

    ValuesConstant.window.width = window.innerWidth;
    ValuesConstant.window.height = window.innerHeight;
    this.setLanguage();
    AppComponent.setGoogleMapLanguage();
  }

  setLanguage(): void {
    let selectedLanguage;
    AppSettingConstant.selectedLanguage.subscribe((language) => {
      selectedLanguage = language;
    });
    this.fuseTranslationLoaderService.loadTranslations(
      navigationEnglish,
      navigationHindi
    );
    this.translateService.addLangs([AppSettingConstant.defaultLanguage]);
    this.translateService.setDefaultLang(AppSettingConstant.defaultLanguage);
    this.translateService.use(AppSettingConstant.defaultLanguage);
    this.languageTranslateService.getLanguages().subscribe(
      (result) => {
        AppSettingConstant.languages.next(result);
        AppSettingConstant.selectedLanguage.next(result[0]);
        const languageKeys = result.map((language) => language.key);
        this.translateService.addLangs(languageKeys);
        if (localStorage.getItem(LocalStorageConstant.languageKey)) {
          const localLanguage = result.find(
            (language) =>
              language.key ===
              localStorage.getItem(LocalStorageConstant.languageKey)
          );
          if (localLanguage) {
            AppSettingConstant.selectedLanguage.next(localLanguage);
          }
        }
        this.translateService.setDefaultLang(selectedLanguage.key);
        this.translateService.use(selectedLanguage.key);
      },
      (err) => {
        throw Error(err.message);
      }
    );
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.unsubscribeAll.next(null);
    this.unsubscribeAll.complete();
  }

  ngAfterViewInit(): void {}

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle sidebar open
   *
   * @param key
   */
  public toggleSidebarOpen(key): void {
    this.fuseSidebarService.getSidebar(key).toggleOpen();
  }

  private checkUserStatus(): void {
    this.checkUser();
    interval(ValuesConstant.checkUserStatusInterval).subscribe(() => {
      if (this.authService.isLoggedIn()) {
        this.checkUser();
      } else {
        clearInterval(ValuesConstant.checkUserStatusInterval);
      }
    });
  }

  private checkUser(): void {
    if (this.authService.isLoggedIn()) {
      this.authService.checkUserStatus().subscribe((res) => {
        if (!res) {
          this.authService.logout();
          this.snackBarService.error(
            this.translatePipe.transform("MESSAGES.auth.userIsDeactivated")
          );
        }
      });
      this.authService.checkUserAllPump().subscribe((res) => {
        if (!res) {
          this.authService.logout();
          this.snackBarService.error(
            this.translatePipe.transform("MESSAGES.Error.L0004")
          );
        }
      });
    }
  }
}
